import React, { useState, useEffect, useMemo } from 'react';
import api from '../../lib/api';
import { formatDate, formatDay } from '../../lib/formatUtils';
import { resolveAttendanceBadge } from '../../lib/attendanceStatus';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Clock, Loader2, LogIn, LogOut, CalendarDays, CheckCircle2, AlertTriangle, PenSquare } from 'lucide-react';
import { RegularizeAttendanceDialog } from '../../components/attendance/RegularizeAttendanceDialog';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export const MyAttendancePage = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({});
  const [month, setMonth] = useState(String(new Date().getMonth() + 1));
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [regularizations, setRegularizations] = useState([]);
  const [regRow, setRegRow] = useState(null);
  // Regularization is only useful within the last 30 days (server enforces
  // this too — we mirror the window so the button doesn't render on rows
  // that would just 400 on submit).
  const REG_WINDOW_DAYS = 30;

  useEffect(() => { fetchAttendance(); fetchRegularizations(); }, [month, year]);

  const fetchAttendance = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/my/attendance?month=${month}&year=${year}`);
      if (res.data.success) {
        setRecords(res.data.data);
        setSummary(res.data.summary || {});
      }
    } catch { setRecords([]); }
    finally { setLoading(false); }
  };

  const fetchRegularizations = async () => {
    try {
      const res = await api.get(`/my/attendance/regularization?month=${month}&year=${year}`);
      setRegularizations(res.data?.data || []);
    } catch { setRegularizations([]); }
  };

  // Map date → latest regularization row for O(1) row lookup during render.
  const regByDate = useMemo(() => {
    const m = {};
    regularizations.forEach(r => {
      // Show the most recent one — the list endpoint sorts desc so we
      // only overwrite if a later-created row exists (defensive).
      if (!m[r.date] || (r.created_at || '') > (m[r.date].created_at || '')) {
        m[r.date] = r;
      }
    });
    return m;
  }, [regularizations]);

  const isRegularizable = (r) => {
    // Reasons a row CAN be regularized by the employee:
    //   1. It's in the past (server also blocks today/future).
    //   2. It's within the 30-day rolling window.
    //   3. Not already Present, not weekly-off, not holiday, not a leave.
    //   4. Not currently pending or approved.
    const today = new Date(); today.setHours(0,0,0,0);
    const d = new Date(r.date + 'T00:00:00');
    if (!(d < today)) return false;
    if ((today - d) / 86400000 > REG_WINDOW_DAYS) return false;
    const status = (r.status || '').toLowerCase();
    if (status === 'present') return false;
    if (r.is_holiday || status === 'holiday') return false;
    if (r.is_weekend || status === 'weekly_off') return false;
    if (['paid_leave','unpaid_leave','on_leave','half_day_paid_leave'].includes(status)) return false;
    const existing = regByDate[r.date];
    if (existing && (existing.status === 'pending' || existing.status === 'approved')) return false;
    return true;
  };

  const formatHours = (h) => {
    if (!h && h !== 0) return '-';
    return `${Math.floor(h)}h ${Math.round((h % 1) * 60)}m`;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="my-attendance-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div><h1 className="text-2xl font-bold">My Attendance</h1><p className="text-muted-foreground">Your attendance history</p></div>
        <div className="flex items-center gap-2">
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-[130px]" data-testid="my-att-month"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-[90px]" data-testid="my-att-year"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="2025">2025</SelectItem><SelectItem value="2026">2026</SelectItem></SelectContent>
          </Select>
        </div>
      </div>

      {/* Widget parity with the admin Attendance page — same 4 cards,
          same math (backend `summary.work_days / present_days / …`). */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center"><CalendarDays className="h-5 w-5 text-primary" /></div><div><p className="text-2xl font-bold" data-testid="my-att-work-days">{summary.work_days ?? 0}</p><p className="text-xs text-muted-foreground">Work Days</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center"><CheckCircle2 className="h-5 w-5 text-emerald-500" /></div><div><p className="text-2xl font-bold" data-testid="my-att-present">{summary.present_days ?? 0}</p><p className="text-xs text-muted-foreground">Present Days</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-red-500/10 flex items-center justify-center"><Clock className="h-5 w-5 text-red-500" /></div><div><p className="text-2xl font-bold" data-testid="my-att-absent">{summary.absent_days ?? 0}</p><p className="text-xs text-muted-foreground">Absent Days</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center"><AlertTriangle className="h-5 w-5 text-amber-500" /></div><div><p className="text-2xl font-bold" data-testid="my-att-leave">{summary.leave_days ?? 0}</p><p className="text-xs text-muted-foreground">Leaves</p></div></div></CardContent></Card>
      </div>

      <Card><CardContent className="p-0">
        {loading ? <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div> :
        records.length === 0 ? <div className="flex flex-col items-center justify-center h-64 text-muted-foreground"><Clock className="h-12 w-12 mb-4 opacity-50" /><p>No attendance records for {MONTHS[parseInt(month) - 1]} {year}</p></div> :
        <Table data-testid="my-attendance-table"><TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Day</TableHead><TableHead>Check In</TableHead><TableHead>Check Out</TableHead><TableHead>Hours</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
        <TableBody>{records.map(r => {
          const d = new Date(r.date + 'T00:00:00');
          const badge = resolveAttendanceBadge(r);
          const reg = regByDate[r.date];
          // "In Progress" should only render when the employee actually
          // punched in that day — otherwise it's a plain non-work / not-
          // checked-in row and deserves an em-dash.
          const checkOutCell = r.check_out
            ? <span className="flex items-center gap-1"><LogOut className="h-3 w-3 text-blue-500" />{r.check_out}</span>
            : r.check_in
              ? <span className="text-amber-500 text-sm">In Progress</span>
              : <span className="text-muted-foreground">-</span>;

          // Actions cell — either an inline regularization-status pill
          // (pending/approved/rejected) or a "Regularize" CTA button.
          let actionCell = null;
          if (reg?.status === 'pending') {
            actionCell = <Badge className="bg-amber-500/10 text-amber-500" data-testid={`reg-status-${r.date}`}>Regularization pending</Badge>;
          } else if (reg?.status === 'approved') {
            actionCell = <Badge className="bg-emerald-500/10 text-emerald-500" data-testid={`reg-status-${r.date}`}>Regularized</Badge>;
          } else if (reg?.status === 'rejected') {
            actionCell = (
              <div className="flex items-center justify-end gap-2">
                <Badge className="bg-red-500/10 text-red-500" title={reg.reviewer_note || ''} data-testid={`reg-status-${r.date}`}>
                  Regularization rejected
                </Badge>
                {isRegularizable(r) && (
                  <Button size="sm" variant="outline" className="h-7 text-xs"
                    onClick={() => setRegRow(r)} data-testid={`reg-btn-${r.date}`}>
                    <PenSquare className="h-3 w-3 mr-1" />Resubmit
                  </Button>
                )}
              </div>
            );
          } else if (isRegularizable(r)) {
            actionCell = (
              <Button size="sm" variant="outline" className="h-7 text-xs"
                onClick={() => setRegRow(r)} data-testid={`reg-btn-${r.date}`}>
                <PenSquare className="h-3 w-3 mr-1" />Regularize
              </Button>
            );
          }

          return (
            <TableRow key={r.id} data-testid={`my-att-row-${r.id}`}>
              <TableCell className="font-medium">
                {formatDate(r.date)}
                {formatDay(r.date) && (
                  <span className="ml-1 text-[10px] text-muted-foreground" data-testid={`my-att-day-${r.date}`}>
                    · {formatDay(r.date)}
                  </span>
                )}
              </TableCell>
              <TableCell className="text-muted-foreground">{d.toLocaleDateString('en-IN', { weekday: 'short' })}</TableCell>
              <TableCell>{r.check_in ? <span className="flex items-center gap-1"><LogIn className="h-3 w-3 text-emerald-500" />{r.check_in}</span> : '-'}</TableCell>
              <TableCell>{checkOutCell}</TableCell>
              <TableCell className="font-mono text-sm">{formatHours(r.total_hours)}</TableCell>
              <TableCell>
                <Badge className={badge.cls} data-testid={`my-att-status-${r.date}`}>{badge.label}</Badge>
              </TableCell>
              <TableCell className="text-right">{actionCell}</TableCell>
            </TableRow>
          );
        })}</TableBody></Table>}
      </CardContent></Card>

      <RegularizeAttendanceDialog
        open={!!regRow}
        onOpenChange={(v) => !v && setRegRow(null)}
        row={regRow}
        onSubmitted={() => { fetchRegularizations(); }}
      />
    </div>
  );
};
